import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import ConfigContext from '../../../contexts/ConfigContext';

// Components
import LogoList from '../content/LogoList';
import Meta from '../../shared/Meta';
import CtaImage from '../content/CtaImage';
import FeaturedTrucks from '../FeaturedTrucks';
import WhyBookWithUs from '../content/WhyBookWithUs';
import Triple from '../content/Triple';
import CostEstimator from '../content/CostEstimator';
import FeaturedCuisines from '../FeaturedCuisines';
import CateringFaq from '../faqs/CateringFaq';
import CateringCta from '../content/CateringCta';
import AnyEvent from '../content/AnyEvent';
import Testimonials from '../content/Testimonials';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';
import tripleStyles from '../content/Triple.module.postcss';

const featuredTrucks = { featured: true };

function Catering() {
  const { site_title, vehicle_type, city_name, site_title_spaces } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();
  const siteTitle = site_title_spaces.slice(0, -4);

  return (
    <div className='CateringPage'>
      <Meta
        title={`Food ${vehicle_type} Catering in ${city_name}`}
        type='website'
        canonical='/catering'
        image='https://static.seattlefoodtruck.com/photos/catering-header.webp'
        description={`Book food ${vehicleType} catering in ${city_name} for corporate events, weddings, parties, and festivals. Get free quotes from top food ${vehicleType}s and compare prices easily.`}
        structuredData={{
          cateringService: {
            name: `Food ${vehicle_type} Catering Services in ${city_name}`,
            description: `Book food ${vehicleType} catering in ${city_name} for corporate events, weddings, parties, and festivals.`,
            url: '/catering',
            serviceType: 'Food Truck Catering',
          },
          breadcrumbs: {
            listItems: [
              { '@type': 'ListItem', position: 2, name: 'Catering', item: '/catering' }
            ]
          }
        }}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering-header.webp' noLazy isResponsive />
          <div className={styles.Cta_copy}>
            <h1>Food {vehicle_type} Catering for Any Event in {city_name}</h1>
            <p>Looking to wow your guests with delicious food and a unique dining experience? Whether you&#39;re planning a <Link to='/food-truck-catering/corporate-catering'>corporate event</Link>, <Link to='/food-truck-catering/wedding-catering'>wedding</Link>, <Link to='/food-truck-catering/birthday-parties'>birthday party</Link>, or <Link to='/food-truck-catering/community-events'>festival</Link>, {site_title} makes it effortless to find and book top-rated food {vehicleType}s. { city_name === 'Seattle' && `Serving ${city_name}, Bellevue, Redmond, Kirkland, Tacoma, and the greater Puget Sound area.` } Simply tell us about your event, receive tailored quotes, and book your perfect match — it&#39;s that easy!</p>
            <p>
              <Link to='/catering/register' className='Button Button--mobile-w100'>Start Your Catering Request</Link>
            </p>
          </div>
        </div>
      </div>

      <section className='Section'>
        <LogoList title='Trusted by leading companies' titleClassName='u-textCenter' className='u-mt1' />
      </section>

      <section className='Section'>
        <div className={cn(styles.Cta, styles.Cta__list)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering2.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <div>
              <h3>🎯 Tailored Matches</h3>
              <ul>
                <li>Free, no-obligation quotes customized for your event.</li>
                <li>Access to hundreds of top food {vehicleType}s in your area.</li>
                <li>An easy way to make your event unique and memorable.</li>
              </ul>
            </div>
            <div>
              <h3>🤝 Expert Event Support</h3>
              <ul>
                <li>We handle the details, so you don&#39;t have to.</li>
                <li>Experienced professionals ensure smooth coordination.</li>
                <li>Dedicated support to assist with logistics & questions.</li>
              </ul>
            </div>
            <div>
              <h3>✅ Stress-Free, Reliable Booking</h3>
              <ul>
                <li>Work with trusted food trucks with proven track records.</li>
                <li>Backup vendors available in case of last-minute changes.</li>
                <li>Secure, hassle-free booking process.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <FeaturedTrucks
        title={`Meet ${city_name}'s Best Food ${vehicleType}s`}
        options={featuredTrucks}
      />

      <WhyBookWithUs />

      <section className='Section--extraPaddingTop'>
        <h2 className='u-textCenter u-mb3'>What makes {siteTitle} different?</h2>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering3.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>🍽️ Unmatched Vendor Selection</h3>
            <p>{site_title} partners with the best food trucks in {city_name}, ensuring every event gets top-tier service and a diverse menu selection. Whether you&#39;re craving <Link to='/food-truck-catering/burger-catering'>gourmet burgers</Link>, <Link to='/food-truck-catering/taco-catering'>authentic tacos</Link>, <Link to='/food-truck-catering/asian-catering'>fresh Asian cuisine</Link>, or <Link to='/food-truck-catering/dessert-catering'>sweet desserts</Link>, we have handpicked vendors to match every taste and dietary need. Our curated network means you&#39;ll work with reliable, highly rated food {vehicleType}s that are passionate about delivering great food and seamless service.</p>
          </div>
        </div>
      </section>

      <section className='Section'>
        <div className={styles.Cta}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering4.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>⚡ Seamless Booking, Zero Hassle</h3>
            <p>No more spending hours calling and emailing vendors — we make booking effortless. Simply enter your event details, and you&#39;ll receive competitive quotes from top {city_name} food {vehicleType}s in minutes. <Link to='/food-truck-catering/prices'>Compare pricing</Link>, chat with vendors, and book with confidence. From the initial inquiry to the final plate served, our team ensures a smooth and hassle-free experience so you can focus on enjoying your event.</p>
          </div>
        </div>
      </section>

      <section className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering5.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>💯 More Than a Booking Service</h3>
            <p>We go beyond just connecting you with food {vehicleType}s — we&#39;re here to make your event a success. Whether it&#39;s a <Link to='/food-truck-catering/office-lunch'>corporate lunch</Link>, <Link to='/food-truck-catering/wedding-catering'>wedding reception</Link>, <Link to='/food-truck-catering/private-parties'>private party</Link>, or <Link to='/food-truck-catering/fundraiser'>fundraiser</Link>, our team helps coordinate logistics, accommodate last-minute changes, and manage special requests. We&#39;re committed to providing reliable, stress-free catering that ensures every guest leaves happy and every event is memorable.</p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <h3 className='u-phosphate'>How It Works — Fast, Easy, and Hassle-Free!</h3>
        <div className={tripleStyles.Triple_wrapper}>
          <Triple
            image='https://static.seattlefoodtruck.com/photos/howitworks1.webp'
            title='1. Answer a few questions'
            text={`Tell us about your event, location, and food preferences so we can match you with the best food ${vehicleType}s.`}
          />
          <Triple
            image='https://static.seattlefoodtruck.com/photos/howitworks2.webp'
            title='2. Receive custom quotes'
            text={`Get personalized offers from top ${city_name} food trucks that fit your budget and event needs.`}
          />
          <Triple
            image='https://static.seattlefoodtruck.com/photos/howitworks3.webp'
            title={`3. Book your ${vehicleType}`}
            text={`Compare quotes, message vendors directly, and lock in your perfect food ${vehicleType} — all in just a few clicks!`}
          />
        </div>
      </section>

      <AnyEvent />

      <Testimonials title="Memories from events we've already made happen" />

      <CostEstimator />

      <section className='Section--extraPadding'>
        <FeaturedCuisines />
      </section>

      <section className='Section--extraPadding'>
        <CateringFaq />
      </section>

      <section className='Section--extraPadding'>
        <CateringCta title={<><span aria-hidden='true'>🚚 </span>Book a Food {vehicle_type} for Your Event!</>} />
      </section>

    </div>
  );
}

export default Catering;
